<template>
  <div class="model-detail-page">
    <!-- Loading -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="#5539EC" />
    </div>

    <!-- Not Found -->
    <ModelNotFound v-else-if="!model" />

    <!-- Model Detail -->
    <template v-else>
      <!-- Breadcrumbs -->
      <v-breadcrumbs :items="breadcrumbs" class="px-0 mb-4">
        <template v-slot:divider>
          <v-icon size="14">mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>

      <v-row>
        <!-- Main Content -->
        <v-col cols="12" md="8">
          <!-- Hero -->
          <div class="mb-8">
            <h1 class="detail-title mb-2">{{ model.displayName }}</h1>
            <p class="detail-provider">{{ model.provider }}</p>
          </div>

          <!-- Top 3 Stat Cards -->
          <v-row class="mb-8">
            <v-col cols="12" sm="4">
              <div class="stat-card">
                <div class="stat-card-label">HumaneScore</div>
                <div class="stat-card-value">{{ model.humaneScore.toFixed(2) }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="stat-card">
                <div class="stat-card-label">Strongest Principle</div>
                <div class="stat-card-value stat-card-text">{{ strongestPrincipleName }}</div>
                <div class="stat-card-sub-score">{{ strongestPrincipleScore }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="stat-card">
                <div class="stat-card-label">Weakest Principle</div>
                <div class="stat-card-value stat-card-text">{{ weakestPrincipleName }}</div>
                <div class="stat-card-sub-score">{{ weakestPrincipleScore }}</div>
              </div>
            </v-col>
          </v-row>

          <!-- Mobile: Nutrition Label -->
          <div class="d-md-none mb-8">
            <NutritionLabel :model="model" />
          </div>

          <!-- Humane Steerability -->
          <div class="section-card mb-8">
            <h2 class="section-title mb-4">Humane Steerability</h2>
            <div class="steerability-score mb-3">
              <span class="steerability-label">Humane Steerability Score:</span>
              <span class="steerability-value">{{ model.steerability.toFixed(2) }}</span>
              <span class="steerability-badge" :class="steerabilityClass">{{ steerabilityLabel }}</span>
            </div>
            <p class="section-text mb-2">{{ steerabilityDescription }}</p>
            <p class="section-text-subtle mb-4">
              Humane steerability measures both how responsive a model is to humane guidance
              (positive steerability) and how resilient it is to adversarial prompting (humane resilience).
            </p>
            <SteerabilityAxis
              :baseline="model.humaneScore"
              :good-persona="model.scores.good_persona.HumaneScore ?? 0"
              :bad-persona="model.scores.bad_persona.HumaneScore ?? 0"
            />
          </div>

          <!-- Detailed Scores by Principle -->
          <div class="section-card mb-8">
            <h2 class="section-title mb-4">Scores by Principle</h2>
            <PrincipleScoreBars :model="model" />
          </div>

        </v-col>

        <!-- Sidebar: Nutrition Label (desktop) -->
        <v-col cols="4" class="d-none d-md-block">
          <div class="sticky-sidebar">
            <NutritionLabel :model="model" />
          </div>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  fetchModelById,
  getStrongestPrinciple,
  getWeakestPrinciple,
  type ModelDetailEntry,
} from '@/utils/modelData';
import NutritionLabel from '@/components/NutritionLabel.vue';
import ModelNotFound from '@/components/ModelNotFound.vue';
import SteerabilityAxis from '@/components/SteerabilityAxis.vue';
import PrincipleScoreBars from '@/components/PrincipleScoreBars.vue';

export default defineComponent({
  name: 'ModelDetailPage',

  components: {
    NutritionLabel,
    ModelNotFound,
    SteerabilityAxis,
    PrincipleScoreBars,
  },

  props: {
    modelId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      model: null as ModelDetailEntry | null,
    };
  },

  computed: {
    breadcrumbs() {
      const items = [
        { title: 'Home', to: { name: 'home' }, disabled: false },
        { title: 'Models', to: { name: 'models' }, disabled: false },
      ];
      if (this.model) {
        items.push({ title: this.model.displayName, to: { name: 'model-detail' }, disabled: true });
      }
      return items;
    },

    steerabilityClass(): string {
      if (!this.model) return '';
      if (this.model.steerability > 0.50) return 'steerability-high';
      if (this.model.steerability >= 0.15) return 'steerability-moderate';
      return 'steerability-limited';
    },

    steerabilityLabel(): string {
      if (!this.model) return '';
      if (this.model.steerability > 0.50) return 'High';
      if (this.model.steerability >= 0.15) return 'Moderate';
      return 'Limited';
    },

    steerabilityDescription(): string {
      if (!this.model) return '';
      const descriptions: Record<string, string> = {
        'gpt-5': 'With a Humane Steerability score of 0.11, this model shows minimal response to humane prompting (+0.08) and resilience to adversarial prompting (−0.03).',
        'gpt-5.1': 'With a Humane Steerability score of 0.09, this model shows minimal response to humane prompting (+0.05) and resilience to adversarial prompting (−0.04).',
        'claude-sonnet-4.5': 'With a Humane Steerability score of 0.12, this model shows resilience to adversarial prompting (+0.02) and modest improvement under humane prompting (+0.13).',
        'claude-opus-4.1': 'With a Humane Steerability score of 0.18, this model shows meaningful improvement under humane prompting (+0.16) and resilience to adversarial prompting (−0.02).',
        'claude-sonnet-4': 'With a Humane Steerability score of 0.34, this model shows meaningful improvement under humane prompting (+0.15) and moderate vulnerability to adversarial prompting (−0.19).',
        'llama-4-maverick': 'With a Humane Steerability score of 0.79, this model shows high vulnerability to adversarial prompting (−0.73) with minimal improvement under humane prompting (+0.06).',
        'deepseek-v3.1-terminus': 'With a Humane Steerability score of 1.20, this model shows very high vulnerability to adversarial prompting (−1.11) with minimal improvement under humane prompting (+0.09).',
        'gemini-3-pro-preview': 'With a Humane Steerability score of 1.38, this model shows meaningful improvement under humane prompting (+0.15) and very high vulnerability to adversarial prompting (−1.24).',
        'llama-3.1-405b-instruct': 'With a Humane Steerability score of 1.17, this model shows very high vulnerability to adversarial prompting (−1.04) with modest improvement under humane prompting (+0.13).',
        'gpt-4.1': 'With a Humane Steerability score of 1.43, this model shows meaningful improvement under humane prompting (+0.15) and very high vulnerability to adversarial prompting (−1.28).',
        'gpt-4o-2024-11-20': 'With a Humane Steerability score of 1.38, this model shows very high vulnerability to adversarial prompting (−1.29) with minimal improvement under humane prompting (+0.09).',
        'gemini-2.5-flash': 'With a Humane Steerability score of 1.45, this model shows very high vulnerability to adversarial prompting (−1.40) with minimal improvement under humane prompting (+0.05).',
        'gemini-2.0-flash-001': 'With a Humane Steerability score of 1.53, this model shows very high vulnerability to adversarial prompting (−1.46) with minimal improvement under humane prompting (+0.07).',
        'gemini-2.5-pro': 'With a Humane Steerability score of 1.63, this model shows modest improvement under humane prompting (+0.14) and very high vulnerability to adversarial prompting (−1.49).',
        'grok-4': 'With a Humane Steerability score of 1.62, this model shows meaningful improvement under humane prompting (+0.21) and very high vulnerability to adversarial prompting (−1.41).',
      };
      return descriptions[this.model.id] ?? '';
    },

    strongestPrincipleName(): string {
      if (!this.model) return 'N/A';
      const strongest = getStrongestPrinciple(this.model);
      return strongest ? strongest.name : 'N/A';
    },

    weakestPrincipleName(): string {
      if (!this.model) return 'N/A';
      const weakest = getWeakestPrinciple(this.model);
      return weakest ? weakest.name : 'N/A';
    },

    strongestPrincipleScore(): string {
      if (!this.model) return '';
      const strongest = getStrongestPrinciple(this.model);
      return strongest ? strongest.score.toFixed(2) : '';
    },

    weakestPrincipleScore(): string {
      if (!this.model) return '';
      const weakest = getWeakestPrinciple(this.model);
      return weakest ? weakest.score.toFixed(2) : '';
    },
  },

  watch: {
    modelId: {
      immediate: true,
      async handler(id: string) {
        this.loading = true;
        this.model = await fetchModelById(id);
        this.loading = false;
      },
    },
  },
});
</script>

<style scoped>
.model-detail-page {
  padding: 0.5rem 0;
}

.detail-title {
  font-family: 'Lora', serif;
  font-size: 2.5rem;
  font-weight: 600;
  line-height: 1.2;
  color: #1a1a1a;
  letter-spacing: -0.02em;
}

.detail-provider {
  font-size: 1.1rem;
  color: #666;
}

.stat-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.25rem;
  text-align: center;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.stat-card-label {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.stat-card-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #5539EC;
  font-variant-numeric: tabular-nums;
}

.stat-card-text {
  font-size: 1rem;
  line-height: 1.3;
}

.stat-card-context {
  font-size: 0.9rem;
  font-weight: 400;
  color: #999;
}

.stat-card-sub-score {
  font-size: 0.9rem;
  font-weight: 600;
  color: #8b7dd8;
  font-variant-numeric: tabular-nums;
  margin-top: 0.35rem;
}

.section-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
}

.section-title {
  font-family: 'Lora', serif;
  font-size: 1.35rem;
  font-weight: 600;
  color: #1a1a1a;
}

.section-text {
  font-size: 0.95rem;
  line-height: 1.7;
  color: #4a4a4a;
}

.section-text-subtle {
  font-size: 0.85rem;
  line-height: 1.6;
  color: #888;
  font-style: italic;
}

.steerability-score {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.steerability-label {
  font-size: 0.95rem;
  color: #4a4a4a;
}

.steerability-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  font-variant-numeric: tabular-nums;
}

.steerability-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.steerability-high {
  background: #f3e5f5;
  color: #7b1fa2;
}

.steerability-moderate {
  background: #f3e5f5;
  color: #7b1fa2;
}

.steerability-limited {
  background: #f3e5f5;
  color: #7b1fa2;
}

.sticky-sidebar {
  position: sticky;
  top: 100px;
}

@media (max-width: 960px) {
  .detail-title {
    font-size: 2rem;
  }

  .section-title {
    font-size: 1.2rem;
  }
}

@media (max-width: 600px) {
  .detail-title {
    font-size: 1.5rem;
  }

  .stat-card-value {
    font-size: 1.5rem;
  }

  .section-card {
    padding: 1rem;
  }
}
</style>
